import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {
  buildSectorUsageLineData,
  isFuelSectorUsageSection,
  isSectorUsageSection,
  openLinkedSector
} from './sectorUsageChartUtils';

function SectorUsageTooltip({ active, payload, valueLabel, fuelMode }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload || {};
  return (
    <Paper sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', minWidth: 180 }} elevation={4}>
      <Typography variant="caption" display="block" color="text.secondary">
        {point.charterNumber
          ? `Charter ${point.charterNumber}`
          : (point.entryDate || point.name)}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {point.charterNumber
          ? (point.aircraft || 'Aircraft')
          : (
            <>
              {point.aircraft || 'Aircraft'}
              {point.sectorNumber !== '' && point.sectorNumber != null ? ` · S${point.sectorNumber}` : ''}
            </>
          )}
      </Typography>
      {point.route && (
        <Typography variant="caption" display="block" color="text.secondary">
          {point.route}
        </Typography>
      )}
      {fuelMode && point.sectorCount > 0 && (
        <Typography variant="caption" display="block" color="text.secondary">
          {point.sectorCount} sector{point.sectorCount !== 1 ? 's' : ''}
          {point.totalFuelKg != null ? ` · ${point.totalFuelKg} kg` : ''}
          {point.totalBlockHours != null ? ` · ${point.totalBlockHours} hr` : ''}
        </Typography>
      )}
      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
        {valueLabel}: {point.value}
      </Typography>
      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
        {point.linkedSectorId ? 'Click to open sector' : 'No linked sector'}
      </Typography>
    </Paper>
  );
}

/**
 * Sector-wise usage line chart for Fuel / Oil consumption report sections.
 * Fuel charts show average kg/hr aggregated per charter.
 * Clicking a point with linkedSectorId opens /ops/sectors/:id.
 */
function SectorUsageLineChart({ section, accentColor = '#1976d2', height = 320 }) {
  const fuelMode = isFuelSectorUsageSection(section?.sectionKey);
  const chartMeta = useMemo(
    () => (isSectorUsageSection(section?.sectionKey) ? buildSectorUsageLineData(section) : null),
    [section]
  );

  if (!chartMeta?.data?.length) return null;

  const { data, truncated, valueLabel, clickableCount } = chartMeta;
  const dense = data.length > 10;
  const xAxisLabel = fuelMode ? 'Charter' : 'Sector';
  const title = fuelMode ? 'Fuel average per hour by charter' : 'Sector-wise usage';
  const description = fuelMode
    ? `${valueLabel} for each charter (fuel used ÷ block hours).`
    : `${valueLabel} for each sector in chronological order.`;
  const countLabel = fuelMode
    ? `${data.length} charter${data.length !== 1 ? 's' : ''}`
    : `${data.length} sector${data.length !== 1 ? 's' : ''}`;

  const handlePointClick = (point) => {
    if (!point?.linkedSectorId) return;
    openLinkedSector(point.linkedSectorId);
  };

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700} color="text.primary">
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, lineHeight: 1.4 }}>
          {description}
          {clickableCount > 0
            ? ' Click a point to open a linked sector.'
            : ' Link sectors in the Technical Log Book to enable click-through.'}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
          {countLabel}
          {truncated ? ' (latest shown)' : ''}
          {clickableCount > 0 ? ` · ${clickableCount} clickable` : ''}
        </Typography>
      </Box>

      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={data}
          margin={{ top: 16, right: 16, left: 4, bottom: dense ? 72 : 48 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10, fill: '#616161' }}
            interval={dense ? Math.max(0, Math.floor(data.length / 12)) : 0}
            angle={dense ? -35 : 0}
            textAnchor={dense ? 'end' : 'middle'}
            height={dense ? 70 : 40}
            axisLine={{ stroke: '#bdbdbd' }}
            tickLine={{ stroke: '#bdbdbd' }}
            label={{
              value: xAxisLabel,
              position: 'insideBottom',
              offset: dense ? -8 : -4,
              style: { fontSize: 11, fill: '#757575', fontWeight: 600 }
            }}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#616161' }}
            axisLine={{ stroke: '#bdbdbd' }}
            tickLine={{ stroke: '#bdbdbd' }}
            label={{
              value: valueLabel,
              angle: -90,
              position: 'insideLeft',
              offset: 12,
              style: { fontSize: 11, fill: '#757575', fontWeight: 600, textAnchor: 'middle' }
            }}
          />
          <Tooltip content={<SectorUsageTooltip valueLabel={valueLabel} fuelMode={fuelMode} />} />
          <Line
            type="monotone"
            dataKey="value"
            stroke={accentColor}
            strokeWidth={2.5}
            dot={(props) => {
              const { cx, cy, payload, index } = props;
              if (cx == null || cy == null) return null;
              const clickable = Boolean(payload?.linkedSectorId);
              return (
                <circle
                  key={`dot-${payload?.name || index}`}
                  cx={cx}
                  cy={cy}
                  r={clickable ? 5 : 3.5}
                  fill={clickable ? accentColor : '#9e9e9e'}
                  stroke="#fff"
                  strokeWidth={1.5}
                  style={{ cursor: clickable ? 'pointer' : 'default' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handlePointClick(payload);
                  }}
                />
              );
            }}
            activeDot={(props) => {
              const { cx, cy, payload, index } = props;
              if (cx == null || cy == null) return null;
              const clickable = Boolean(payload?.linkedSectorId);
              return (
                <circle
                  key={`active-dot-${payload?.name || index}`}
                  cx={cx}
                  cy={cy}
                  r={7}
                  fill={clickable ? accentColor : '#9e9e9e'}
                  stroke="#fff"
                  strokeWidth={2}
                  style={{ cursor: clickable ? 'pointer' : 'default' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handlePointClick(payload);
                  }}
                />
              );
            }}
          />
        </LineChart>
      </ResponsiveContainer>

      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Tip: grey points are not linked to an ops sector yet.
        </Typography>
      </Stack>
    </Box>
  );
}

export default SectorUsageLineChart;
